import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
} from '@mui/material';
import type { Estimate, EstimateSettings } from '../../types/estimateDetail';
import { updateEstimate } from '../../services/estimateService';

interface EstimateSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  estimate: Estimate;
  onSave?: () => void;
}

const DEFAULT_SETTINGS: EstimateSettings = {
  currency: 'zł',
  precisionUnitPrice: 2,
  precisionRmsPrice: 3,
  precisionQuantity: 3,
  precisionExpenses: 4,
  precisionNorm: 6,
};

const EstimateSettingsDrawer: React.FC<EstimateSettingsDrawerProps> = ({
  open,
  onClose,
  estimate,
  onSave,
}) => {
  const s = estimate.settings ?? DEFAULT_SETTINGS;
  const [formData, setFormData] = useState({
    currency: s.currency,
    precisionUnitPrice: s.precisionUnitPrice.toString(),
    precisionRmsPrice: s.precisionRmsPrice.toString(),
    precisionQuantity: s.precisionQuantity.toString(),
    precisionExpenses: s.precisionExpenses.toString(),
    precisionNorm: s.precisionNorm.toString(),
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const s0 = estimate.settings ?? DEFAULT_SETTINGS;
      setFormData({
        currency: s0.currency,
        precisionUnitPrice: s0.precisionUnitPrice.toString(),
        precisionRmsPrice: s0.precisionRmsPrice.toString(),
        precisionQuantity: s0.precisionQuantity.toString(),
        precisionExpenses: s0.precisionExpenses.toString(),
        precisionNorm: s0.precisionNorm.toString(),
      });
    }
  }, [open, estimate.settings]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateEstimate(estimate.id, {
        currency: formData.currency,
        precision_unit_price: parseInt(formData.precisionUnitPrice, 10) || 2,
        precision_rms_price: parseInt(formData.precisionRmsPrice, 10) || 3,
        precision_quantity: parseInt(formData.precisionQuantity, 10) || 3,
        precision_expenses: parseInt(formData.precisionExpenses, 10) || 4,
        precision_norm: parseInt(formData.precisionNorm, 10) || 6,
      } as any);
      onSave?.();
      onClose();
    } catch (err) {
      console.error('Błąd zapisywania ustawień kosztorysu:', err);
    } finally {
      setLoading(false);
    }
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      height: 36,
      backgroundColor: '#fff',
      borderRadius: '4px',
      '& fieldset': { borderColor: '#d4d4d9' },
      '&:hover fieldset': { borderColor: '#595966' },
      '&.Mui-focused fieldset': { borderColor: '#595966 !important' },
      '&.Mui-focused': { boxShadow: 'none', outline: 'none' },
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Roboto, sans-serif',
      fontSize: '14px',
      color: '#2D2D32',
    },
  } as const;

  const FieldLabel: React.FC<{ text: string }> = ({ text }) => (
    <Typography
      sx={{
        fontFamily: 'Roboto, sans-serif',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '16px',
        color: '#2D2D32',
        mb: 0.5,
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Nagłówek */}
        <Box
          sx={{
            pl: 4,
            pr: 4,
            pt: '22px',
            pb: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 500,
              fontSize: '18px',
              color: '#2D2D32',
            }}
          >
            Ustawienia kosztorysu
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': { pointerEvents: 'none' },
            }}
          >
            <Box
              component="img"
              src="/assets/close.svg"
              alt="Zamknij"
              sx={{
                width: 13,
                height: 13,
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            />
          </IconButton>
        </Box>

        {/* Zawartość */}
        <Box sx={{ px: 4, mt: '24px', flex: 1, overflowY: 'auto', pb: 2 }}>
          {/* Waluta */}
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '16px',
              fontWeight: 500,
              color: '#2D2D32',
              mb: 2,
            }}
          >
            Waluta
          </Typography>
          <Box sx={{ mb: 3 }}>
            <FieldLabel text="Waluta" />
            <TextField
              size="small"
              fullWidth
              value={formData.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              variant="outlined"
              placeholder="zł"
              sx={textFieldSx}
            />
          </Box>

          {/* Dokładność obliczeń */}
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '16px',
              fontWeight: 500,
              color: '#2D2D32',
              mb: 2,
            }}
          >
            Dokładność obliczeń (ilość miejsc po przecinku)
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <FieldLabel text="Cena jednostkowa pozycji" />
              <TextField
                size="small"
                fullWidth
                type="number"
                value={formData.precisionUnitPrice}
                onChange={(e) => handleInputChange('precisionUnitPrice', e.target.value)}
                variant="outlined"
                inputProps={{ min: 0, max: 10 }}
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <FieldLabel text="Cena RMS" />
              <TextField
                size="small"
                fullWidth
                type="number"
                value={formData.precisionRmsPrice}
                onChange={(e) => handleInputChange('precisionRmsPrice', e.target.value)}
                variant="outlined"
                inputProps={{ min: 0, max: 10 }}
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <FieldLabel text="Ilość" />
              <TextField
                size="small"
                fullWidth
                type="number"
                value={formData.precisionQuantity}
                onChange={(e) => handleInputChange('precisionQuantity', e.target.value)}
                variant="outlined"
                inputProps={{ min: 0, max: 10 }}
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <FieldLabel text="Wyliczone ilość nakładów (Nakłady)" />
              <TextField
                size="small"
                fullWidth
                type="number"
                value={formData.precisionExpenses}
                onChange={(e) => handleInputChange('precisionExpenses', e.target.value)}
                variant="outlined"
                inputProps={{ min: 0, max: 10 }}
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <FieldLabel text="Normy z katalogów KNR" />
              <TextField
                size="small"
                fullWidth
                type="number"
                value={formData.precisionNorm}
                onChange={(e) => handleInputChange('precisionNorm', e.target.value)}
                variant="outlined"
                inputProps={{ min: 0, max: 10 }}
                sx={textFieldSx}
              />
            </Box>
          </Box>
        </Box>

        {/* Stopka z przyciskami */}
        <Box
          sx={{
            px: 4,
            py: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            boxShadow: '0px -4px 4px rgba(0,0,0,0.05)',
            borderTop: '1px solid #EDEDED',
          }}
        >
          <Button onClick={onClose} variant="text" disabled={loading}>
            Anuluj
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: '#020242',
              '&:hover': { backgroundColor: '#030360' },
            }}
          >
            Zapisz
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default EstimateSettingsDrawer;
